import type { BaseQueryFn } from '@reduxjs/toolkit/query'

import {
  baseApi,
  resourceListTag,
  resourceScopeTag,
  resourceTag,
  type ResourceTag,
  type TransportError,
  type TransportMeta,
  type TransportRequest,
} from '@krp/client-runtime'

import { fail, isRecord, onecEnvelopeDecoder, type OneCEnvelope } from '../../../shared/api/onecEnvelope'
import { writeCommandBody, type WriteCommandIdentity } from '../../../shared/api/writeCommand'
import { normalizeRid } from '../../../shared/model/scopeKeys'
import { ONEC_STRUCTURE_TAG, structureTag } from '../../structure/api/structureApi'
import type {
  AllocationMatrix,
  AllocationProblem,
  AuditEntry,
  CursorPage,
  OneCReceipt,
  PostingAttempt,
  PostingPreview,
  PostingProgress,
  PriceTypesDirectory,
  Supply,
  SupplyCommandKind,
  SupplyCommandResult,
  SupplyLine,
  SupplyListItem,
  SupplyStatus,
} from '../model/types'
import {
  ONEC_SUPPLY_SCHEMAS,
  decodeAllocations,
  decodeCreated,
  decodeAllocationFill,
  decodeDone,
  decodeFactor,
  decodeFieldHistory,
  decodeFill,
  decodeHistory,
  decodeLines,
  decodePostingLog,
  decodePreview,
  decodePriceTypes,
  decodeProblems,
  decodeProgress,
  decodeReceipts,
  decodeStartedPosting,
  decodeSupply,
  decodeSupplyList,
} from './contracts'
import { MAX_SNAPSHOT_PAGES, RECEIPTS_PAGE_TAKE } from '../model/limits'
import { ONEC_SUPPLY_URLS } from './urls'

/**
 * Endpoints поставок 1С в ОБЩЕМ `baseApi` (R04): имена `onecSupply*`, rid —
 * аргумент каждого запроса (часть ключа кэша), каждый тег — `<rid>:<сущность>`
 * плюс тег области ресурса. Ответ проверяется декодером, включая rid конверта
 * и rid каждой ссылки 1С внутри.
 *
 * Курсорные списки (gateway-paging-v1) читаются ЦЕЛЫМ снимком: страницы
 * 1..N за один запрос кэша с одним курсором. «Показать ещё» — новый N, а не
 * склейка страниц разных снимков: курсор привязан к снимку, и перечитанная
 * первая страница с хвостом от старого снимка показала бы смесь.
 */
export const ONEC_SUPPLY_TAG = 'OneCSupply' as const

/** Страница курсора — не больше 200 строк (B3b `SupplyPages.MaxTake`). */
export const SUPPLY_PAGE_TAKE = 200
/** Строки поставки читаются целиком; дальше — честный отказ, а не обрезанная таблица. */
export const MAX_LINE_PAGES = MAX_SNAPSHOT_PAGES

export interface SupplyResourceArgs {
  resourceId: string
}

export interface SupplyArgs {
  resourceId: string
  supplyId: string
}

export interface SupplyListArgs {
  resourceId: string
  status: SupplyStatus | ''
  number: string
  /** Сколько страниц снимка показать. */
  pages: number
}

export interface SupplyLinesArgs extends SupplyArgs {
  search: string
  onlyProblems: boolean
}

export interface SupplyPagedArgs extends SupplyArgs {
  pages: number
}

export interface SupplyReceiptsArgs extends SupplyResourceArgs {
  pages: number
}

export interface SupplyFieldHistoryArgs extends SupplyArgs {
  field: string
  lineNumber: number | null
}

/** Склеенный снимок курсора: строки и «есть ли ещё». */
export interface Snapshot<T> {
  items: T[]
  complete: boolean
  observedAt: string
}

export interface SupplyCommandArgs {
  resourceId: string
  supplyId: string
  kind: SupplyCommandKind
  lineId: string | null
  data: unknown
  query: Record<string, string> | null
  identity: WriteCommandIdentity
}

type Transport = BaseQueryFn<TransportRequest, unknown, TransportError, object, TransportMeta>

/** Прочитать до `pages` страниц одного снимка; ошибка любой страницы — ошибка всего чтения. */
async function readSnapshot<T>(
  request: (cursor: string | null) => TransportRequest,
  pages: number,
  transport: (request: TransportRequest) => ReturnType<Transport>,
  overflow: string | null,
): Promise<{ data: Snapshot<T> } | { error: TransportError }> {
  const items: T[] = []
  let cursor: string | null = null
  let observedAt = ''
  for (let page = 0; page < pages; page += 1) {
    const answer = await transport(request(cursor))
    if ('error' in answer && answer.error !== undefined) return { error: answer.error }
    const envelope = answer.data as OneCEnvelope<CursorPage<T>>
    const decoded = envelope.data
    if (page === 0) observedAt = decoded.observedAt
    items.push(...decoded.items)
    if (decoded.complete || decoded.nextCursor === null) return { data: { items, complete: true, observedAt } }
    cursor = decoded.nextCursor
  }
  if (overflow !== null) {
    return {
      error: { kind: 'validation', code: 'too-many-lines', message: overflow, correlationId: '', retryable: false },
    }
  }
  return { data: { items, complete: false, observedAt } }
}

const supplyTags = (resourceId: string, supplyId: string) => [
  resourceTag(ONEC_SUPPLY_TAG, normalizeRid(resourceId), supplyId.toLowerCase()),
  resourceScopeTag(normalizeRid(resourceId)),
]

const listTags = (resourceId: string) => [
  resourceListTag(ONEC_SUPPLY_TAG, normalizeRid(resourceId)),
  resourceScopeTag(normalizeRid(resourceId)),
]


interface Route {
  url: string
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  schema: string
  decode: (raw: unknown) => SupplyCommandResult
  /** Тело `data`, если команда шлёт не то, что дал builder (по умолчанию — как есть). */
  data?: (data: unknown) => unknown
}

const done = (raw: unknown): SupplyCommandResult => ({ kind: 'done', ...decodeDone(raw) })

const allocated = (raw: unknown): SupplyCommandResult => ({ kind: 'allocated', fill: decodeAllocationFill(raw) })

/**
 * Fill-команды распределения служба по умолчанию считает предпросмотром (`dryRun ?? true`): кнопка окна —
 * применение, поэтому `dryRun: false` ставится явно, а не берётся из умолчания службы (дефект B-1 приёмки U9).
 */
const applyFill = (data: unknown) => ({ ...(isRecord(data) ? data : {}), dryRun: false })

/** Адрес, метод, схема и разбор ответа каждой команды — одна таблица, без ветвлений по экрану. */
function routeOf(args: SupplyCommandArgs): Route {
  const { resourceId: rid, supplyId: id } = args
  // Строка нужна только командам строки: остальные её не передают.
  const line = () => args.lineId ?? fail('у команды строки нет lineId')
  const urls = ONEC_SUPPLY_URLS
  const s = ONEC_SUPPLY_SCHEMAS
  switch (args.kind) {
    case 'create':
      return { url: urls.list(rid), method: 'POST', schema: s.create, decode: (raw) => ({ kind: 'created', supply: decodeCreated(raw) }) }
    case 'update':
      return { url: urls.supply(rid, id), method: 'PATCH', schema: s.update, decode: done }
    case 'delete':
      return { url: urls.supply(rid, id), method: 'DELETE', schema: s.delete, decode: done }
    case 'clone':
      return { url: urls.clone(rid, id), method: 'POST', schema: s.clone, decode: (raw) => ({ kind: 'created', supply: decodeCreated(raw) }) }
    case 'import-invoice':
      return { url: urls.importInvoice(rid, id), method: 'POST', schema: s.importInvoice, decode: (raw) => ({ kind: 'filled', fill: decodeFill(raw) }) }
    case 'add-line':
      return { url: urls.lines(rid, id), method: 'POST', schema: s.addLine, decode: done }
    case 'update-line':
      return { url: urls.line(rid, id, line()), method: 'PATCH', schema: s.updateLine, decode: done }
    case 'delete-line':
      return { url: urls.line(rid, id, line()), method: 'DELETE', schema: s.deleteLine, decode: done }
    case 'set-ignored':
      return { url: urls.lineIgnored(rid, id, line()), method: 'PUT', schema: s.setIgnored, decode: done }
    case 'match':
      return { url: urls.match(rid, id), method: 'POST', schema: s.match, decode: done }
    case 'override-match':
      return { url: urls.lineMatch(rid, id, line()), method: 'POST', schema: s.overrideMatch, decode: done }
    case 'reset-match':
      return { url: urls.lineMatch(rid, id, line()), method: 'DELETE', schema: s.resetMatch, decode: done }
    case 'mark-new':
      return { url: urls.lineMarkNew(rid, id, line()), method: 'POST', schema: s.markNew, decode: done }
    case 'price-factor':
      return { url: urls.priceFactor(rid, id), method: 'POST', schema: s.priceFactor, decode: (raw) => ({ kind: 'factor', factor: decodeFactor(raw) }) }
    case 'set-prices':
      return { url: urls.prices(rid, id), method: 'PUT', schema: s.setPrices, decode: done }
    case 'set-line-allocations':
      return { url: urls.lineAllocations(rid, id, line()), method: 'PUT', schema: s.setLineAllocations, decode: done }
    case 'default-warehouse':
      return { url: urls.defaultWarehouse(rid, id), method: 'PUT', schema: s.defaultWarehouse, decode: done }
    case 'fill-all-to-one':
      return { url: urls.fill(rid, id, 'all-to-one'), method: 'POST', schema: s.fillAllToOne, decode: allocated, data: applyFill }
    case 'fill-evenly':
      return { url: urls.fill(rid, id, 'evenly'), method: 'POST', schema: s.fillEvenly, decode: allocated, data: applyFill }
    case 'fill-clear':
      return { url: urls.fill(rid, id, 'clear'), method: 'POST', schema: s.fillClear, decode: allocated, data: applyFill }
    case 'post':
      return {
        url: urls.post(rid, id),
        method: 'POST',
        schema: s.post,
        decode: (raw) => ({ kind: 'posting', posting: decodeStartedPosting(id)(raw) }),
      }
  }
}

/** Ответ команды: конверт своей базы или пустое тело (204). */
function commandDecoder(resourceId: string, decode: (raw: unknown) => SupplyCommandResult) {
  const envelope = onecEnvelopeDecoder(resourceId, decode)
  return (raw: unknown): SupplyCommandResult => (raw === null ? decode(null) : envelope(raw).data)
}

export const suppliesApi = baseApi
  .enhanceEndpoints({ addTagTypes: [ONEC_SUPPLY_TAG, ONEC_STRUCTURE_TAG] })
  .injectEndpoints({
    endpoints: (build) => ({
      onecSupplies: build.query<Snapshot<SupplyListItem>, SupplyListArgs>({
        queryFn: ({ resourceId, status, number, pages }, _api, _extra, transport) =>
          readSnapshot(
            (cursor) => ({
              url: ONEC_SUPPLY_URLS.list(resourceId),
              params: {
                take: SUPPLY_PAGE_TAKE,
                ...(status !== '' ? { status } : {}),
                ...(number.trim() !== '' ? { number: number.trim() } : {}),
                ...(cursor !== null ? { cursor } : {}),
              },
              decode: onecEnvelopeDecoder(resourceId, decodeSupplyList(resourceId)),
            }),
            Math.max(1, pages),
            transport,
            null,
          ),
        providesTags: (_result, _error, { resourceId }) => listTags(resourceId),
      }),
      onecSupply: build.query<OneCEnvelope<Supply>, SupplyArgs>({
        query: ({ resourceId, supplyId }) => ({
          url: ONEC_SUPPLY_URLS.supply(resourceId, supplyId),
          decode: onecEnvelopeDecoder(resourceId, decodeSupply(resourceId)),
        }),
        providesTags: (_result, _error, { resourceId, supplyId }) => supplyTags(resourceId, supplyId),
      }),
      onecSupplyLines: build.query<Snapshot<SupplyLine>, SupplyLinesArgs>({
        queryFn: ({ resourceId, supplyId, search, onlyProblems }, _api, _extra, transport) =>
          readSnapshot(
            (cursor) => ({
              url: ONEC_SUPPLY_URLS.lines(resourceId, supplyId),
              params: {
                take: SUPPLY_PAGE_TAKE,
                ...(search.trim() !== '' ? { search: search.trim() } : {}),
                ...(onlyProblems ? { onlyProblems: true } : {}),
                ...(cursor !== null ? { cursor } : {}),
              },
              decode: onecEnvelopeDecoder(resourceId, decodeLines(resourceId)),
            }),
            MAX_LINE_PAGES,
            transport,
            `В поставке больше ${MAX_LINE_PAGES * SUPPLY_PAGE_TAKE} строк: окно их целиком не покажет, сузьте поиск.`,
          ),
        providesTags: (_result, _error, { resourceId, supplyId }) => supplyTags(resourceId, supplyId),
      }),
      onecSupplyPriceTypes: build.query<OneCEnvelope<PriceTypesDirectory>, SupplyArgs>({
        query: ({ resourceId, supplyId }) => ({
          url: ONEC_SUPPLY_URLS.priceTypes(resourceId, supplyId),
          decode: onecEnvelopeDecoder(resourceId, decodePriceTypes(resourceId)),
        }),
        providesTags: (_result, _error, { resourceId, supplyId }) => supplyTags(resourceId, supplyId),
      }),
      onecSupplyAllocations: build.query<OneCEnvelope<AllocationMatrix>, SupplyArgs>({
        query: ({ resourceId, supplyId }) => ({
          url: ONEC_SUPPLY_URLS.allocations(resourceId, supplyId),
          decode: onecEnvelopeDecoder(resourceId, decodeAllocations(resourceId)),
        }),
        providesTags: (_result, _error, { resourceId, supplyId }) => [
          ...supplyTags(resourceId, supplyId),
          // Склады столбцов служба читает из 1С на лету: «Повторить» у выбора склада перечитывает и их.
          structureTag(resourceId),
        ],
      }),
      onecSupplyValidate: build.query<OneCEnvelope<AllocationProblem[]>, SupplyArgs>({
        query: ({ resourceId, supplyId }) => ({
          url: ONEC_SUPPLY_URLS.allocationsValidate(resourceId, supplyId),
          decode: onecEnvelopeDecoder(resourceId, decodeProblems),
        }),
        providesTags: (_result, _error, { resourceId, supplyId }) => supplyTags(resourceId, supplyId),
      }),
      onecSupplyPreview: build.query<OneCEnvelope<PostingPreview>, SupplyArgs>({
        query: ({ resourceId, supplyId }) => ({
          url: ONEC_SUPPLY_URLS.preview(resourceId, supplyId),
          decode: onecEnvelopeDecoder(resourceId, decodePreview(resourceId)),
        }),
        providesTags: (_result, _error, { resourceId, supplyId }) => [
          ...supplyTags(resourceId, supplyId),
          // Склады столбцов служба читает из 1С на лету: «Повторить» у выбора склада перечитывает и их.
          structureTag(resourceId),
        ],
      }),
      onecSupplyPostingStatus: build.query<OneCEnvelope<PostingProgress>, SupplyArgs>({
        query: ({ resourceId, supplyId }) => ({
          url: ONEC_SUPPLY_URLS.postingStatus(resourceId, supplyId),
          decode: onecEnvelopeDecoder(resourceId, decodeProgress(supplyId)),
        }),
        providesTags: (_result, _error, { resourceId, supplyId }) => supplyTags(resourceId, supplyId),
      }),
      onecSupplyPostingLog: build.query<OneCEnvelope<PostingAttempt[]>, SupplyArgs>({
        query: ({ resourceId, supplyId }) => ({
          url: ONEC_SUPPLY_URLS.postingLog(resourceId, supplyId),
          decode: onecEnvelopeDecoder(resourceId, decodePostingLog),
        }),
        providesTags: (_result, _error, { resourceId, supplyId }) => supplyTags(resourceId, supplyId),
      }),
      onecSupplyHistory: build.query<Snapshot<AuditEntry>, SupplyPagedArgs>({
        queryFn: ({ resourceId, supplyId, pages }, _api, _extra, transport) =>
          readSnapshot(
            (cursor) => ({
              url: ONEC_SUPPLY_URLS.history(resourceId, supplyId),
              params: { take: SUPPLY_PAGE_TAKE, ...(cursor !== null ? { cursor } : {}) },
              decode: onecEnvelopeDecoder(resourceId, decodeHistory),
            }),
            Math.max(1, pages),
            transport,
            null,
          ),
        providesTags: (_result, _error, { resourceId, supplyId }) => supplyTags(resourceId, supplyId),
      }),
      onecSupplyFieldHistory: build.query<OneCEnvelope<AuditEntry[]>, SupplyFieldHistoryArgs>({
        query: ({ resourceId, supplyId, field, lineNumber }) => ({
          url: ONEC_SUPPLY_URLS.fieldHistory(resourceId, supplyId),
          params: { field, ...(lineNumber !== null ? { lineNumber } : {}) },
          decode: onecEnvelopeDecoder(resourceId, decodeFieldHistory),
        }),
        providesTags: (_result, _error, { resourceId, supplyId }) => supplyTags(resourceId, supplyId),
      }),
      onecSupplyReceipts: build.query<Snapshot<OneCReceipt>, SupplyReceiptsArgs>({
        queryFn: ({ resourceId, pages }, _api, _extra, transport) =>
          readSnapshot(
            (cursor) => ({
              url: ONEC_SUPPLY_URLS.receipts(resourceId),
              params: { take: RECEIPTS_PAGE_TAKE, ...(cursor !== null ? { cursor } : {}) },
              decode: onecEnvelopeDecoder(resourceId, decodeReceipts),
            }),
            Math.max(1, pages),
            transport,
            null,
          ),
        providesTags: (_result, _error, { resourceId }) => listTags(resourceId),
      }),

      // --- команды: ключ и operationId приходят от операции слайса, не создаются здесь ---
      onecSupplyCommand: build.mutation<SupplyCommandResult, SupplyCommandArgs>({
        query: (args) => {
          const route = routeOf(args)
          return {
            url: route.url,
            method: route.method,
            ...(args.query !== null ? { params: args.query } : {}),
            body: writeCommandBody(route.schema, args.identity, route.data === undefined ? args.data : route.data(args.data)),
            idempotencyKey: args.identity.idempotencyKey,
            decode: commandDecoder(args.resourceId, route.decode),
          }
        },
        invalidatesTags: (_result, _error, args) => {
          // Отказ ДО записи ничего не меняет, но исход неизвестен чаще, чем кажется: перечитываем всегда.
          const tags: ResourceTag<typeof ONEC_SUPPLY_TAG>[] = [
            resourceListTag(ONEC_SUPPLY_TAG, normalizeRid(args.resourceId)),
          ]
          if (args.supplyId !== '') tags.push(resourceTag(ONEC_SUPPLY_TAG, normalizeRid(args.resourceId), args.supplyId.toLowerCase()))
          return tags
        },
      }),
    }),
  })

export const {
  onecSupplies,
  onecSupply,
  onecSupplyLines,
  onecSupplyPriceTypes,
  onecSupplyAllocations,
  onecSupplyValidate,
  onecSupplyPreview,
  onecSupplyPostingStatus,
  onecSupplyPostingLog,
  onecSupplyHistory,
  onecSupplyFieldHistory,
  onecSupplyReceipts,
  onecSupplyCommand,
} = suppliesApi.endpoints
